import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import type { EventInfoEntity, EventInvolvement } from '../../types/api';
import { borderRadius, colors, spacing, typography } from '../../styles/theme';
import EntityAvatar from '../EntityAvatar';
import { DetailSection, DetailSectionTitle } from './DetailSection';

const Group = styled.div`
  margin-bottom: ${spacing[4]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const GroupTitle = styled.h3`
  margin: 0 0 ${spacing[2]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
`;

const OrganizationList = styled.ul`
  display: grid;
  gap: ${spacing[2]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const OrganizationItem = styled.li`
  min-width: 0;
`;

const organizationRowStyles = `
  min-width: 0;
  min-height: 36px;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  text-decoration: none;
`;

const OrganizationLink = styled(Link)`
  ${organizationRowStyles}

  &:hover,
  &:focus-visible {
    color: ${colors.primary[700]};
    text-decoration: underline;
  }
`;

const OrganizationLabel = styled.span`
  ${organizationRowStyles}
`;

const OrganizationName = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
`;

const ToggleButton = styled.button`
  width: 100%;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1]};
  margin-top: ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.background.primary};
  color: ${colors.primary[700]};
  font: inherit;
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${colors.primary[50]};
  }
`;

function EntityList({ entities, label }: { entities: EventInfoEntity[]; label: string }) {
  if (entities.length === 0) return null;
  return (
    <OrganizationList aria-label={label}>
      {entities.map(entity => {
        const name = entity.displayName || entity.entityName;
        const content = (
          <>
            <span aria-hidden="true">
              <EntityAvatar
                name={name}
                entityType={entity.entityType}
                logoUrl={entity.logoUrl}
                size={32}
              />
            </span>
            <OrganizationName>{name}</OrganizationName>
          </>
        );
        return (
          <OrganizationItem key={entity.entityId}>
            {entity.canonicalUrl ? (
              <OrganizationLink to={entity.canonicalUrl}>{content}</OrganizationLink>
            ) : (
              <OrganizationLabel>{content}</OrganizationLabel>
            )}
          </OrganizationItem>
        );
      })}
    </OrganizationList>
  );
}

export interface EventInvolvementSectionProps {
  involvement?: EventInvolvement;
  initialVisible?: number;
  onToggle?: (expanded: boolean) => void;
}

export const EventInvolvementSection: React.FC<EventInvolvementSectionProps> = ({
  involvement,
  initialVisible = 12,
  onToggle,
}) => {
  const [expanded, setExpanded] = useState(false);
  if (!involvement) return null;

  const hosts = involvement.hosts || [];
  const hostIds = new Set(hosts.map(entity => entity.entityId));
  const organizers = (involvement.organizers || [])
    .filter(entity => !hostIds.has(entity.entityId));
  const organizations = involvement.organizations || [];
  if (hosts.length === 0 && organizers.length === 0 && organizations.length === 0) return null;

  const visibleOrganizations = expanded
    ? organizations
    : organizations.slice(0, initialVisible);
  const canExpand = organizations.length > initialVisible;
  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    onToggle?.(next);
  };

  return (
    <DetailSection>
      <DetailSectionTitle>Who's involved</DetailSectionTitle>
      {hosts.length > 0 && (
        <Group>
          <GroupTitle>Hosted by</GroupTitle>
          <EntityList entities={hosts} label="Event hosts" />
        </Group>
      )}
      {organizers.length > 0 && (
        <Group>
          <GroupTitle>Organized by</GroupTitle>
          <EntityList entities={organizers} label="Event organizers" />
        </Group>
      )}
      {organizations.length > 0 && (
        <Group>
          <GroupTitle>Organizations at this event</GroupTitle>
          <div id="event-involvement-organizations">
            <EntityList entities={visibleOrganizations} label="Organizations at this event" />
          </div>
          {canExpand && (
            <ToggleButton
              type="button"
              aria-expanded={expanded}
              aria-controls="event-involvement-organizations"
              onClick={toggle}
            >
              {expanded ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
              {expanded ? 'Show fewer' : `Show all ${organizations.length}`}
            </ToggleButton>
          )}
        </Group>
      )}
    </DetailSection>
  );
};

export default EventInvolvementSection;